import React from 'react';
import styles from "./DryContactIOStatus.module.css";
import { Box, Card, Divider, ListSubheader, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { COLOR_GRAY_ICON } from '../../constants/color-codes';
import { IOStatusBadge } from '../../components/IOStatusBadge/IOStatusBadge';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import * as PXBColors from '@brightlayer-ui/colors';
import { DEVICE_TYPE } from '../enum/ENUM';
import { DeviceData } from '../../types/device';
type Props = {
    deviceType: string;
    systemDetail: DeviceData;
    handleToggleDryContactIOSettingDrawer: (isOpen: boolean) => void;
}

export const DryContactIOStatus: React.FC<Props> = (props: Props) => {
    const { t } = useLanguageLocale();
    const inputConfigDetailLabels: { [key: string]: string } = {
        '1': 'DRY_CONTACT.IOSTATUS.NOT_CONFIGURED',
        '2': 'DRY_CONTACT.NORMALLY_OPEN',
        '3': 'DRY_CONTACT.IOSTATUS.REST_ORDER',
        '4': 'DRY_CONTACT.IOSTATUS.MAINTAINED_MODE_OFFLINE1',
        '5': 'DRY_CONTACT.IOSTATUS.MAINTAINED_MODE_OFFLINE2',
        '6': 'DRY_CONTACT.IOSTATUS.MAINTAINED_MODE_OFFLINE3',
        '7': 'DRY_CONTACT.IOSTATUS.MAINTAINED_MODE_OFFLINE4',
        '8': 'DRY_CONTACT.IOSTATUS.START_FUNCTIONAL_TEST',
        '9': 'DRY_CONTACT.IOSTATUS.STOP_FT_DT',
        '10': 'DRY_CONTACT.IOSTATUS.EXTERNAL_FAILURE',
        '11': 'DRY_CONTACT.IOSTATUS.ACTIVATE_IA_MODELINE1',
        '12': 'DRY_CONTACT.IOSTATUS.ACTIVATE_IA_MODELINE2',
        '13': 'DRY_CONTACT.IOSTATUS.ACTIVATE_IA_MODELINE3',
        '14': 'DRY_CONTACT.IOSTATUS.ACTIVATE_IA_MODELINE4',
        '15': 'DRY_CONTACT.NORMALLY_CLOSED',
    };
    const outputConfigDetailLabels: { [key: string]: string } = {
        '1': 'DRY_CONTACT.IOSTATUS.NOT_CONFIGURED',
        '2': 'DRY_CONTACT.NORMALLY_OPEN',
        '3': 'DRY_CONTACT.IOSTATUS.ONE_LUMINAIRE_FAILURE',
        '4': 'DRY_CONTACT.IOSTATUS.THREE_CONSECUTIVE_LUMINAIRE_FAILURE',
        '5': 'DUALSMART.DS_COMMUNICATION_FAILURE',
        '6': 'DUALSMART.DS_BATTERY_OPERATION',
        '7': 'DRY_CONTACT.IOSTATUS.FUNCTIONAL_TEST',
        '8': 'DEVICE_DETAILS.DURATION_TEST',
        '9': 'DRY_CONTACT.IOSTATUS.CHARGING_FAILURE',
        '10': 'DRY_CONTACT.IOSTATUS.FAILURE_FT_DT',
        '11': 'DRY_CONTACT.NORMALLY_CLOSED',
    };
    const inputConfigDetailWirelessLabels: { [key: string]: string } = {
        '1': 'DRY_CONTACT.IOSTATUS.NOT_CONFIGURED',
        '2': 'DRY_CONTACT.NORMALLY_OPEN',
        '3': 'DRY_CONTACT.IOSTATUS.REST_ORDER',
        '4': 'CONFIG_IO.SWITCH_MAINTAINED_MODE_OFF_FOR',
        '5': 'DRY_CONTACT.ACTIVATE_IA',
        '6': 'DUALSMART.DS_START_FUNCTIONAL_TEST',
        '7': 'DRY_CONTACT.IOSTATUS.STOP_FTDT',
        '8': 'DRY_CONTACT.IOSTATUS.EXTERNAL_FAILURE',
        '9': 'DRY_CONTACT.NORMALLY_CLOSED',
    };
    const outputConfigDetailWirelessLabels: { [key: string]: string } = {
        '1': 'DRY_CONTACT.IOSTATUS.NOT_CONFIGURED',
        '2': 'DRY_CONTACT.NORMALLY_OPEN',
        '3': 'DRY_CONTACT.IOSTATUS.ONE_LUMINAIRE_FAILURE',
        '4': 'DRY_CONTACT.IOSTATUS.LUMINAIRE_ALERT_THRESHOLD',
        '5': 'DUALSMART.DS_COMMUNICATION_FAILURE',
        '6': 'ATPLUS_LOGBOOK_EVENTNAME.TLAT_PLUS_BATTERY_FAILURE',
        '7': 'DRY_CONTACT.IOSTATUS.CHARGING_FAILURE',
        '8': 'DRY_CONTACT.IOSTATUS.FUNCTIONAL_TEST',
        '9': 'DUALSMART.DS_DURATION_TEST',
        '10': 'DRY_CONTACT.IOSTATUS.FAILURE_FT_DT',
        '11': 'DRY_CONTACT.NORMALLY_CLOSED',
    };
   const communicationInputMethod : number[] = props.deviceType === DEVICE_TYPE.DEVICE_TYPE_WIRELESS ? [2,9] : [2,15] ;
   const communicationOutputMethod : number[] =  [2,11] ;
   const outputConfigDetailCommonLabels : {[key: string]: string} = props.deviceType === DEVICE_TYPE.DEVICE_TYPE_WIRELESS ? outputConfigDetailWirelessLabels : outputConfigDetailLabels;
   const inputConfigDetailCommonLabels : {[key: string]: string} = props.deviceType === DEVICE_TYPE.DEVICE_TYPE_WIRELESS ? inputConfigDetailWirelessLabels : inputConfigDetailLabels;
   const zoneRange : number[] = props.deviceType === DEVICE_TYPE.DEVICE_TYPE_WIRELESS ? [18, 33] : [];

    const renderConfigDetailsOutput = (details?: number[]) => {
        if (!details || details.length === 0) return t('MANAGE_USERS.NOT_APPLICABLE_SLASH');
        const firstLine = details
            .filter(val => val === communicationOutputMethod[0] || val === communicationOutputMethod[1])
            .map(val => t(outputConfigDetailCommonLabels[val.toString()] || val.toString()))
            .join(', ');
        const secondLine = details
            .filter(val => val < communicationOutputMethod[1] && val !== communicationOutputMethod[0])
            .map(val => t(outputConfigDetailCommonLabels[val.toString()] || val.toString()))
            .join('\n');
        return (
            <>
                <span>{firstLine || t('MANAGE_USERS.NOT_APPLICABLE_SLASH')}</span>
                {secondLine.includes(t('DRY_CONTACT.IOSTATUS.LUMINAIRE_ALERT_THRESHOLD'))
                    ?
                    <Typography variant="body2" component="div" style={{ display: 'block',whiteSpace: 'pre-line' }}>
                        {secondLine.replace(
                        t('DRY_CONTACT.IOSTATUS.LUMINAIRE_ALERT_THRESHOLD'),
                        `${t('DRY_CONTACT.IOSTATUS.LUMINAIRE_ALERT_THRESHOLD')}- ${props.systemDetail.dryContactOutput?.[0]?.relayLumAlertThreshold ?? ''}`
                    )}
                    </Typography>
                    :
                    <Typography variant="body2" component="div" style={{ display: 'block', whiteSpace: 'pre-line' }}>
                        {secondLine}
                    </Typography>
                }
            </>
        );
    };
    const renderConfigDetailsInput = (details?: number[]) => {
        if (!details || details.length === 0) return t('MANAGE_USERS.NOT_APPLICABLE_SLASH');
        const firstLine = details
            .filter(val => val === communicationInputMethod[0] || val === communicationInputMethod[1])
            .map(val => t(inputConfigDetailCommonLabels[val.toString()] || val.toString()))
            .join(', ');
        const secondLine = details
            .filter(val => val < communicationInputMethod[1] &&  val !== communicationInputMethod[0])
            .map(val => t(inputConfigDetailCommonLabels[val.toString()] || val.toString()))
            .join(', ');
        const thirdLine = details
            .filter(val => val >= zoneRange[0] && val <= zoneRange[1])
            .map(val => (val - zoneRange[0] + 1).toString())
            .join(', ');
        return (
            <>
                <span>{firstLine || t('MANAGE_USERS.NOT_APPLICABLE_SLASH')}</span>
                {secondLine && (
                    <Typography variant="body2" component="div" style={{ display: 'block' }}>
                        {secondLine}
                    </Typography>
                )}
                {thirdLine && props.deviceType === DEVICE_TYPE.DEVICE_TYPE_WIRELESS && (
                <Typography variant="body2" component="div" style={{ display: 'block' }}>
                        {thirdLine.split(',')?.length === 16 ? t('DEVICE_DETAILS.ALL_ZONES') : `${t('DEVICE_DETAILS.ZONES')}: ${thirdLine}`}
                    </Typography>
                )}
            </>
        );
    };


    return (
        <>
            {
                [DEVICE_TYPE.DEVICE_TYPE_CGLINE, DEVICE_TYPE.DEVICE_TYPE_WIRELESS, DEVICE_TYPE.DEVICE_TYPE_AT].includes(props.deviceType as DEVICE_TYPE) &&
                <Box
                    display="flex"
                    style={{
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        height: 'wrap',
                        display: 'flex',
                        flexWrap: 'wrap',
                        width: '100%',
                        alignItems: 'center',
                        marginTop: 10
                    }}
                    data-testid="luminaries-list"
                >
                    <Card className={styles.card} data-testid='device-dry-contact-io-status-card-container'>
                        <ListSubheader
                            color="primary"
                            component="div"
                            className={styles.listSubHeader}
                        >
                            <Typography
                                variant="subtitle1"
                                className={styles.subTitle}
                                data-testid='io-status-title'
                                style={{
                                    color: PXBColors.blue[400]
                                }}
                            >
                                {t('DRY_CONTACT.TITLE')}
                            </Typography>
                            {props.deviceType !== DEVICE_TYPE.DEVICE_TYPE_WIRELESS && <SettingsIcon onClick={() => { props.handleToggleDryContactIOSettingDrawer(true) }} style={{ color: COLOR_GRAY_ICON, cursor: 'pointer' }} />}
                        </ListSubheader>
                        <Divider />
                        <div className={styles.content}>
                            <div className={styles.row}>
                                <div className={styles.contentItem}>
                                    <div>
                                        <Typography variant="subtitle1" fontWeight="bold">{t('DRY_CONTACT.S1_S2')}</Typography>
                                        {props.systemDetail.dryContactS1S2 && (
                                            <Typography variant='body2'>{t('DRY_CONTACT.BLOCK_THE_SYSTEM')}</Typography>
                                        )}
                                    </div>
                                    <div>
                                        <IOStatusBadge
                                            status={
                                                props.systemDetail.dryContactS1S2 ? "1" : "0"
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={styles.row}>
                                <div className={styles.contentItem}>
                                    <div>
                                        <Typography variant="subtitle1">
                                            <span style={{ fontWeight: "bold" }}>{t('DRY_CONTACT.INPUT_1')}</span>
                                            {": "}
                                            {renderConfigDetailsInput(props.systemDetail.dryContactInput?.[0]?.configDetails)}
                                        </Typography>
                                    </div>
                                    <div>
                                        {
                                            <IOStatusBadge status={props.systemDetail.dryContactInput?.[0]?.status !== undefined ? props.systemDetail.dryContactInput[0].status.toString() : ''} />
                                        }
                                    </div>
                                </div>
                                <div className={styles.contentItem}>
                                    <div>
                                        <Typography variant="subtitle1">
                                            <span style={{ fontWeight: "bold" }}>{t('DRY_CONTACT.OUTPUT_1')}</span>
                                            {": "}
                                            {renderConfigDetailsOutput(props.systemDetail.dryContactOutput?.[0]?.configDetails)}
                                        </Typography>
                                    </div>
                                    <div>
                                        {
                                            <IOStatusBadge status={props.systemDetail.dryContactOutput?.[0]?.status !== undefined ? props.systemDetail.dryContactOutput[0].status.toString() : ''} />
                                        }
                                    </div>
                                </div>
                            </div>
                            {props.deviceType !== DEVICE_TYPE.DEVICE_TYPE_WIRELESS && <div className={styles.row}>
                                <div className={styles.contentItem}>
                                    <div>
                                        <Typography variant="subtitle1">
                                            <span style={{ fontWeight: "bold" }}>{t('DRY_CONTACT.INPUT_2')}</span>
                                            {": "}
                                            {renderConfigDetailsInput(props.systemDetail.dryContactInput?.[1]?.configDetails)}
                                        </Typography>
                                    </div>
                                    <div>
                                        {
                                            <IOStatusBadge status={props.systemDetail.dryContactInput?.[1]?.status !== undefined ? props.systemDetail.dryContactInput[1].status.toString() : ''} />
                                        }
                                    </div>
                                </div>
                                <div className={styles.contentItem}>
                                    <div>
                                        <Typography variant="subtitle1">
                                            <span style={{ fontWeight: "bold" }}>{t('DRY_CONTACT.OUTPUT_2')}</span>
                                            {": "}
                                            {renderConfigDetailsOutput(props.systemDetail.dryContactOutput?.[1]?.configDetails)}
                                        </Typography>
                                    </div>
                                    <div>
                                        {
                                            <IOStatusBadge status={props.systemDetail.dryContactOutput?.[1]?.status !== undefined ? props.systemDetail.dryContactOutput[1].status.toString() : ''} />
                                        }
                                    </div>
                                </div>
                            </div>}
                        </div>
                    </Card >
                </Box>
            }
        </>
    );
};
